/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  FlatList,
  Pressable,
  SafeAreaView,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const { width } = Dimensions.get('window');

const SearchScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [recentSearches, setRecentSearches] = useState([
    'Engineering courses',
    'Mechanical design',
    'Electrical systems',
    'Project management',
    'AutoCAD training',
  ]);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));

  useEffect(() => {
    loadUserData();
    loadRecentSearches();
    startAnimations();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // Enhanced gradient color schemes
  const gradients = {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
  };

  // Mixed color scheme for different categories with gradients
  const categoryGradients = {
    'Civil Engineering': gradients.orange,
    'Mechanical Engineering': gradients.green,
    'Electrical Engineering': gradients.blue,
    'Software Engineering': gradients.purple,
    'Chemical Engineering': gradients.danger,
    'Environmental Engineering': gradients.info,
    'Project Management': gradients.warning,
    'Materials Science': gradients.teal,
    'Biomedical Engineering': gradients.success,
    'Aerospace Engineering': gradients.primary,
  };

  const getGradientForCategory = (category) => {
    return categoryGradients[category] || gradients.orange;
  };

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadRecentSearches = async () => {
    try {
      const stored = await AsyncStorage.getItem('recentSearches');
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  };

  const saveRecentSearch = async (query) => {
    try {
      const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
      setRecentSearches(updated);
      await AsyncStorage.setItem('recentSearches', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Student';
  };

  // Enhanced sample data with mixed colors
  const allResults = [
    {
      id: '1',
      title: 'Structural Analysis Fundamentals',
      category: 'Civil Engineering',
      type: 'Course',
      rating: 4.8,
      students: 1250,
      instructor: 'Dr. Sarah Johnson',
      duration: '8 weeks',
      level: 'Intermediate',
      isFree: false,
      price: 79,
      description: 'Master the principles of structural analysis for buildings and bridges.',
      skills: ['Beam Analysis', 'Load Calculations', 'Stress Distribution'],
    },
    {
      id: '2',
      title: 'Thermodynamics Engineering Approach',
      category: 'Mechanical Engineering',
      type: 'Book',
      rating: 4.6,
      students: 980,
      instructor: 'Prof. Michael Roberts',
      duration: '650 pages',
      level: 'Advanced',
      isFree: false,
      price: 45,
      description: 'Comprehensive guide to thermodynamics principles and applications.',
      skills: ['Heat Transfer', 'Energy Systems', 'Fluid Mechanics'],
    },
    {
      id: '3',
      title: 'Circuit Design and Analysis',
      category: 'Electrical Engineering',
      type: 'Course',
      rating: 4.9,
      students: 2100,
      instructor: 'Dr. Emma Wilson',
      duration: '12 weeks',
      level: 'Beginner',
      isFree: true,
      description: 'Learn electrical circuit design from basic to advanced concepts.',
      skills: ['Circuit Analysis', 'PCB Design', 'Signal Processing'],
    },
    {
      id: '4',
      title: 'Python for Engineers',
      category: 'Software Engineering',
      type: 'Course',
      rating: 4.7,
      students: 1560,
      instructor: 'Prof. James Thompson',
      duration: '10 weeks',
      level: 'Intermediate',
      isFree: false,
      price: 69,
      description: 'Programming fundamentals and engineering applications using Python.',
      skills: ['Python Programming', 'Data Analysis', 'Automation'],
    },
    {
      id: '5',
      title: 'Chemical Process Design',
      category: 'Chemical Engineering',
      type: 'Course',
      rating: 4.5,
      students: 890,
      instructor: 'Dr. Robert Chen',
      duration: '6 weeks',
      level: 'Advanced',
      isFree: true,
      description: 'Design and optimization of chemical manufacturing processes.',
      skills: ['Process Design', 'Safety Analysis', 'Cost Optimization'],
    },
    {
      id: '6',
      title: 'Environmental Impact Assessment',
      category: 'Environmental Engineering',
      type: 'Course',
      rating: 4.4,
      students: 750,
      instructor: 'Dr. Lisa Anderson',
      duration: '8 weeks',
      level: 'Intermediate',
      isFree: false,
      price: 85,
      description: 'Assess and mitigate environmental impacts of engineering projects.',
      skills: ['Impact Assessment', 'Sustainability', 'Regulations'],
    },
    {
      id: '7',
      title: 'PRINCE2 Project Management',
      category: 'Project Management',
      type: 'Certification',
      rating: 4.8,
      students: 1800,
      instructor: 'Prof. David Taylor',
      duration: '4 weeks',
      level: 'Professional',
      isFree: false,
      price: 120,
      description: 'Industry-standard project management methodology certification.',
      skills: ['PRINCE2', 'Risk Management', 'Team Leadership'],
    },
    {
      id: '8',
      title: 'Materials Science Handbook',
      category: 'Materials Science',
      type: 'Book',
      rating: 4.6,
      students: 650,
      instructor: 'Dr. Jennifer Lee',
      duration: '800 pages',
      level: 'Reference',
      isFree: false,
      price: 55,
      description: 'Comprehensive reference for materials properties and applications.',
      skills: ['Material Properties', 'Testing Methods', 'Selection Criteria'],
    },
  ];

  const popularCategories = [
    { id: '1', title: 'Civil Engineering', icon: 'bridge', gradient: gradients.orange },
    { id: '2', title: 'Mechanical Engineering', icon: 'cog', gradient: gradients.green },
    { id: '3', title: 'Electrical Engineering', icon: 'lightning-bolt', gradient: gradients.blue },
    { id: '4', title: 'Software Engineering', icon: 'code-braces', gradient: gradients.purple },
    { id: '5', title: 'Chemical Engineering', icon: 'flask', gradient: gradients.danger },
    { id: '6', title: 'Project Management', icon: 'chart-gantt', gradient: gradients.warning },
  ];

  const filterOptions = ['All', 'Courses', 'Books', 'Certifications'];

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    saveRecentSearch(searchQuery.trim());

    // Filter results based on search query and selected filter
    let filteredResults = allResults.filter(
      item =>
        String(item.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(item.category ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(item.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (selectedFilter !== 'All') {
      const filterMap = {
        'Courses': 'Course',
        'Books': 'Book',
        'Certifications': 'Certification'
      };
      filteredResults = filteredResults.filter(item => item.type === filterMap[selectedFilter]);
    }

    setSearchResults(filteredResults);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  const clearAllRecentSearches = async () => {
    try {
      setRecentSearches([]);
      await AsyncStorage.removeItem('recentSearches');
    } catch (error) {
      console.error('Error clearing recent searches:', error);
    }
  };

  const handleCategoryPress = (category) => {
    setSearchQuery(category.title);
    setTimeout(() => {
      handleSearch();
    }, 100);
  };

  const handleRecentSearchPress = (search) => {
    setSearchQuery(search);
    setTimeout(() => {
      handleSearch();
    }, 100);
  };

  const handleResultPress = (item) => {
    if (item.type === 'Course') {
      navigation.navigate('CourseDetailsScreen', { course: item });
    } else if (item.type === 'Book') {
      navigation.navigate('BookDetailScreen', { book: item });
    } else if (item.type === 'Certification') {
      Alert.alert('Certification', `Learn more about ${item.title} certification program.`);
    }
  };

  const renderFilterTabs = () => (
    <View style={styles.filterContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScroll}>
        {filterOptions.map((filter) => (
          <TouchableOpacity
            key={filter}
            style={[
              styles.filterTab,
              selectedFilter === filter && styles.activeFilterTab,
            ]}
            onPress={() => setSelectedFilter(filter)}
          >
            {selectedFilter === filter ? (
              <LinearGradient
                colors={gradients.primary}
                style={styles.filterGradient}
              >
                <Text style={styles.activeFilterTabText}>{filter}</Text>
              </LinearGradient>
            ) : (
              <Text style={styles.filterTabText}>{filter}</Text>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchResult = (item) => {
    const categoryGradient = getGradientForCategory(item.category);
    
    return (
      <Animated.View
        key={item.id}
        style={[
          styles.resultCard,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <TouchableOpacity onPress={() => handleResultPress(item)}>
          <View style={styles.resultHeader}>
            <LinearGradient
              colors={categoryGradient}
              style={styles.resultIconGradient}
            >
              <MaterialCommunityIcons 
                name={item.type === 'Course' ? 'book-open-variant' :
                      item.type === 'Book' ? 'book' :
                      item.type === 'Certification' ? 'certificate' : 'school'} 
                size={28} 
                color="#ffffff" 
              />
            </LinearGradient>
            <View style={styles.resultInfo}>
              <Text style={styles.resultTitle}>{item.title}</Text>
              <Text style={styles.resultInstructor}>👨‍🏫 by {item.instructor}</Text>
              <View style={styles.resultMeta}>
                <LinearGradient
                  colors={categoryGradient}
                  style={styles.categoryBadgeGradient}
                >
                  <Text style={styles.categoryText}>{item.category}</Text>
                </LinearGradient>
                <View style={styles.typeBadge}>
                  <Text style={styles.typeText}>{item.type}</Text>
                </View>
              </View>
            </View>
            <View style={styles.resultStats}>
              <View style={styles.ratingContainer}>
                <MaterialCommunityIcons name="star" size={18} color="#fdb913" />
                <Text style={styles.ratingText}>{item.rating}</Text>
              </View>
              <Text style={styles.studentsText}>👥 {item.students?.toLocaleString() || 'N/A'}</Text>
            </View>
          </View>

          <Text style={styles.resultDescription}>{item.description}</Text>

          <View style={styles.skillsContainer}>
            {item.skills.map((skill, index) => (
              <View key={index} style={styles.skillTag}>
                <Text style={styles.skillText}>🔧 {skill}</Text>
              </View>
            ))}
          </View>

          <View style={styles.resultFooter}>
            <View style={styles.resultDetails}>
              <Text style={styles.durationText}>⏱️ {item.duration}</Text>
              <Text style={styles.levelText}>📊 {item.level}</Text>
            </View>
            <View style={styles.priceContainer}>
              <Text style={[styles.priceText, { color: item.isFree ? '#10b981' : '#1f2937' }]}>
                {item.isFree ? '🆓 Free' : `💷 £${item.price}`}
              </Text>
              <TouchableOpacity onPress={() => handleResultPress(item)}>
                <LinearGradient
                  colors={categoryGradient}
                  style={styles.enrollButtonGradient}
                >
                  <Text style={styles.enrollButtonText}>
                    {item.type === 'Course' ? '📚 Enroll' : item.type === 'Book' ? '📖 Read' : '🎓 Apply'}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  const renderWelcomeSection = () => (
    <Animated.View 
      style={[
        styles.welcomeSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.welcomeGradient}
      >
        <Text style={styles.welcomeTitle}>🔍 Hello, {getUserName()}!</Text>
        <Text style={styles.welcomeSubtitle}>✨ What would you like to learn today?</Text>
      </LinearGradient>
    </Animated.View>
  );

  const renderRecentSearches = () => (
    recentSearches.length > 0 && (
      <Animated.View 
        style={[
          styles.sectionContainer,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>🕒 Recent Searches</Text>
          <TouchableOpacity onPress={clearAllRecentSearches}>
            <LinearGradient
              colors={gradients.danger}
              style={styles.clearAllGradient}
            >
              <Text style={styles.clearAllText}>Clear All</Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>

        <View style={styles.recentSearchesContainer}>
          {recentSearches.map((search, index) => (
            <TouchableOpacity
              key={index}
              style={styles.recentSearchItem}
              onPress={() => handleRecentSearchPress(search)}
            >
              <LinearGradient
                colors={gradients.info}
                style={styles.recentSearchIconGradient}
              >
                <MaterialCommunityIcons name="history" size={16} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.recentSearchText}>{search}</Text>
              <MaterialCommunityIcons name="arrow-top-left" size={16} color="#9ca3af" />
            </TouchableOpacity>
          ))}
        </View>
      </Animated.View>
    )
  );

  const renderPopularCategories = () => (
    <Animated.View 
      style={[
        styles.sectionContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={styles.sectionTitle}>🔥 Popular Categories</Text>

      <View style={styles.categoriesGrid}>
        {popularCategories.map(category => (
          <TouchableOpacity
            key={category.id}
            style={styles.categoryCard}
            onPress={() => handleCategoryPress(category)}
          >
            <LinearGradient
              colors={category.gradient}
              style={styles.categoryIconGradient}
            >
              <MaterialCommunityIcons
                name={category.icon}
                size={32}
                color="#ffffff"
              />
            </LinearGradient>
            <Text style={styles.categoryTitle}>{category.title}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </Animated.View>
  );

  const renderTrendingSearches = () => (
    <Animated.View 
      style={[
        styles.sectionContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={styles.sectionTitle}>📈 Trending in UK Engineering</Text>

      <View style={styles.trendingContainer}>
        {[
          { term: 'Renewable Energy', gradient: gradients.green },
          { term: 'AI in Engineering', gradient: gradients.purple },
          { term: 'Sustainable Design', gradient: gradients.info },
          { term: 'Digital Twins', gradient: gradients.orange },
          { term: 'Green Building', gradient: gradients.success },
        ].map((trend, index) => (
          <TouchableOpacity
            key={index}
            style={styles.trendingItem}
            onPress={() => {
              setSearchQuery(trend.term);
              setTimeout(() => handleSearch(), 100);
            }}
          >
            <LinearGradient
              colors={trend.gradient}
              style={styles.trendingIconGradient}
            >
              <MaterialCommunityIcons name="trending-up" size={18} color="#ffffff" />
            </LinearGradient>
            <Text style={styles.trendingText}>{trend.term}</Text>
            <MaterialCommunityIcons name="chevron-right" size={18} color="#9ca3af" />
          </TouchableOpacity>
        ))}
      </View>
    </Animated.View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {/* Enhanced Header */}
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.header}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={28} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>🔍 Search</Text>
        <TouchableOpacity style={styles.menuButton}>
          <MaterialCommunityIcons name="dots-vertical" size={28} color="#ffffff" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Enhanced Search Container */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <MaterialCommunityIcons
            name="magnify"
            size={24}
            color="#6b7280"
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            placeholder="🔍 Search courses, books, certifications..."
            placeholderTextColor="#9ca3af"
            value={searchQuery}
            onChangeText={setSearchQuery}
            returnKeyType="search"
            onSubmitEditing={handleSearch}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={clearSearch} style={styles.clearButton}>
              <MaterialCommunityIcons name="close-circle" size={24} color="#6b7280" />
            </TouchableOpacity>
          )}
        </View>

        <TouchableOpacity onPress={handleSearch}>
          <LinearGradient
            colors={gradients.primary}
            style={styles.searchButtonGradient}
          >
            <MaterialCommunityIcons name="magnify" size={20} color="#ffffff" />
          </LinearGradient>
        </TouchableOpacity>
      </View>

      {renderFilterTabs()}

      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {searchResults.length > 0 ? (
          <View style={styles.resultsContainer}>
            <View style={styles.resultsHeader}>
              <Text style={styles.resultsTitle}>🎯 Search Results</Text>
              <Text style={styles.resultsCount}>
                ✅ {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
              </Text>
            </View>

            {searchResults.map(renderSearchResult)}
          </View>
        ) : (
          <View>
            {renderWelcomeSection()}
            {renderRecentSearches()}
            {renderPopularCategories()}
            {renderTrendingSearches()}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  menuButton: {
    padding: 8,
  },
  searchContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 16,
    paddingHorizontal: 16,
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: '#1f2937',
  },
  clearButton: {
    padding: 4,
  },
  searchButtonGradient: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  filterContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  filterScroll: {
    paddingHorizontal: 20,
  },
  filterTab: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 25,
    backgroundColor: '#f8fafc',
    marginRight: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  activeFilterTab: {
    backgroundColor: 'transparent',
  },
  filterGradient: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterTabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  activeFilterTabText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
  },
  scrollContainer: {
    flex: 1,
  },
  welcomeSection: {
    margin: 20,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  welcomeGradient: {
    padding: 24,
    alignItems: 'center',
  },
  welcomeTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  welcomeSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
  },
  resultsContainer: {
    padding: 20,
  },
  resultsHeader: {
    marginBottom: 20,
  },
  resultsTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 8,
  },
  resultsCount: {
    fontSize: 16,
    color: '#6b7280',
  },
  resultCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  resultHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  resultIconGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  resultInfo: {
    flex: 1,
  },
  resultTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 6,
  },
  resultInstructor: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 12,
  },
  resultMeta: {
    flexDirection: 'row',
    gap: 8,
  },
  categoryBadgeGradient: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#ffffff',
  },
  typeBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  typeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6b7280',
  },
  resultStats: {
    alignItems: 'flex-end',
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  ratingText: {
    marginLeft: 6,
    fontSize: 16,
    fontWeight: '700',
    color: '#1f2937',
  },
  studentsText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  resultDescription: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 22,
    marginBottom: 16,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  skillTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f8fafc',
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  skillText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6b7280',
  },
  resultFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  resultDetails: {
    flex: 1,
  },
  durationText: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 4,
    fontWeight: '500',
  },
  levelText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  priceText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  enrollButtonGradient: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  enrollButtonText: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: 14,
  },
  sectionContainer: {
    backgroundColor: '#ffffff',
    margin: 20,
    padding: 24,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  clearAllGradient: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  clearAllText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
  },
  recentSearchesContainer: {
    gap: 12,
  },
  recentSearchItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  recentSearchIconGradient: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  recentSearchText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  categoriesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  categoryCard: {
    width: '48%',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f8fafc',
    borderRadius: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  categoryIconGradient: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  categoryTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1f2937',
    textAlign: 'center',
  },
  trendingContainer: {
    gap: 12,
  },
  trendingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  trendingIconGradient: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  trendingText: {
    flex: 1,
    fontSize: 15,
    color: '#374151',
    fontWeight: '600',
  },
});

export default SearchScreen;
